import React, { createContext, useContext } from "react";
import { usePaymentState, PaymentState } from "../hooks/usePaymentState";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import { useAuth } from "../../../context/AuthContext";
import { isSuperAdmin, isInternal } from "../../../utils/permission";

interface BillingStateValue {
  /** Billing state of the selected Brand. null means healthy, or unknown. */
  paymentState: PaymentState;
  /** Staff keep full access to a paused Brand so they can help fix it. */
  isStaff: boolean;
  /** The paywall replaces the page content. */
  showPaywall: boolean;
  /** The banner is shown above the page content. */
  showBanner: boolean;
  refetch: () => void;
}

const BillingStateContext = createContext<BillingStateValue>({
  paymentState: null,
  isStaff: false,
  showPaywall: false,
  showBanner: false,
  refetch: () => {},
});

/**
 * One source of billing state for the whole shell (SGL-283).
 *
 * A context rather than a hook call in each component, so the poll runs once
 * per session instead of once per consumer, and so the banner and the paywall
 * can never disagree about what state the Brand is in.
 */
export const BillingStateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedCustomerId } = useCustomerWorkspace();
  const { userGroups } = useAuth();
  const { paymentState, refetch } = usePaymentState(selectedCustomerId);

  // Staff are never paywalled out of a customer's Brand. Support exists to help
  // the person whose payment failed, and locking the team out of the account
  // they are trying to fix would be the wrong way round.
  const isStaff = isSuperAdmin(userGroups) || isInternal(userGroups);

  const showPaywall = paymentState === "paused" && !isStaff;

  // past_due always warns. `paused` normally shows the paywall instead — but a
  // staff member walking past the paywall still needs to know the Brand is
  // paused, or they will debug "publishing does nothing" from scratch.
  const showBanner =
    paymentState === "past_due" || (paymentState === "paused" && isStaff);

  return (
    <BillingStateContext.Provider
      value={{ paymentState, isStaff, showPaywall, showBanner, refetch }}
    >
      {children}
    </BillingStateContext.Provider>
  );
};

export const useBillingState = (): BillingStateValue =>
  useContext(BillingStateContext);
